import { AvTabState } from "../../shared/types/av-state";

import { createAppSelector } from "./base";
import { selectActiveClassSession } from "./class-session";
import { selectCurrentClassUserId } from "./class-user";

import { ClassSessionCategory } from "components/server-types";
import { type RootState } from "store";

const MAX_RECENT_SPEAKERS = 5;

// the selectMainAvTab selector is used to determine if the current tab is the main AV tab
// we do this by looking for the user that has the unique_user_id set in the pubsub_virtual_class_state
export const selectIsMainAvTab = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState) => state.auth.websocket?.uniqueId,
  ],
  (pubsub_virtual_class_state_users, uniqueId) => {
    if (!uniqueId) {
      return false;
    }

    for (const classUserId in pubsub_virtual_class_state_users) {
      if (pubsub_virtual_class_state_users[classUserId]?.activeWebsocketId === uniqueId) {
        return true;
      }
    }

    return false;
  },
);

export const selectVirtualUserState = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string | null) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    if (!classUserId || !pubsub_virtual_class_state.users[classUserId]) {
      return null;
    }

    return pubsub_virtual_class_state.users[classUserId];
  },
);

export const selectAvTabState = (state: RootState) => {
  return state.pubsub_virtual_class_state.av_tab_state;
};

export const selectStableIsMainAvTab = (state: RootState) => {
  return state.pubsub_virtual_class_state.av_tab_state !== AvTabState.NotMain;
};

export const selectMaxParticipantBarSlots = (state: RootState) => {
  return state.pubsub_virtual_class_state.max_participant_bar_slots;
};

export const selectClassUserIdsPresentInClass = createAppSelector(
  (state: RootState) => state.pubsub_virtual_class_state.users,
  (users) => {
    return Object.keys(users).filter((classUserId) => users[classUserId]?.isPresent);
  },
);

export const selectIsVirtualUserPresentInClass = createAppSelector(
  [(state) => state.pubsub_virtual_class_state.users, (state, classUserId: string) => classUserId],
  (users, classUserId) => {
    return users[classUserId]?.isPresent ?? false;
  },
);

export const selectRecentSpeakerClassUserIds = createAppSelector(
  (state: RootState) => state.pubsub_virtual_class_state.users,
  (users) => {
    // Get all entries with lastSpokeAt - users speaking now will have lastSpokeAt set
    const sorted = Object.entries(users)
      .filter(([, userState]) => userState.lastSpokeAt)
      .filter(([, userState]) => userState.isPresent)
      .sort(([, a], [, b]) => {
        // Speaking users first
        if (a.isSpeaking !== b.isSpeaking) {
          return a.isSpeaking ? -1 : 1;
        }

        // Then by lastSpokeAt desc
        return (b.lastSpokeAt ?? 0) - (a.lastSpokeAt ?? 0);
      })
      .map(([classUserId]) => classUserId);

    return sorted.slice(0, MAX_RECENT_SPEAKERS);
  },
);

export const selectIsVirtualUserSpeaking = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const virtualUser = pubsub_virtual_class_state.users[classUserId];
    return virtualUser?.isSpeaking ?? false;
  },
);

export const selectClassStateLastUpdatedAt = (state: RootState) => {
  const res = state.pubsub_virtual_class_state.last_updated_at;
  return res;
};

export const selectIsPublishedAudioEnabled = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const res = pubsub_virtual_class_state.users[classUserId]?.isAudioEnabled ?? false;
    return res;
  },
);

export const selectVirtualUserMicError = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const res = pubsub_virtual_class_state.users[classUserId]?.micError ?? null;
    return res;
  },
);

export const selectVirtualUserCameraError = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const res = pubsub_virtual_class_state.users[classUserId]?.cameraError ?? null;
    return res;
  },
);

export const selectVirtualUserConnectionState = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const res = pubsub_virtual_class_state.users[classUserId]?.connectionState ?? null;
    return res;
  },
);

export const selectVirtualUserPubsubPingRtt = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const res = pubsub_virtual_class_state.users[classUserId]?.pubsubPingRtt ?? null;
    return res;
  },
);

export const selectVirtualUserShareDBPingRtt = createAppSelector(
  [(state) => state.pubsub_virtual_class_state, (state, classUserId: string) => classUserId],
  (pubsub_virtual_class_state, classUserId) => {
    const res = pubsub_virtual_class_state.users[classUserId]?.shareDBPingRtt ?? null;
    return res;
  },
);

export const selectIsObservingVirtualClass = createAppSelector(
  [(state) => state.pubsub_virtual_class_state.observing_session_id, selectActiveClassSession],
  (observingVirtualClassId, currentClassSession) =>
    currentClassSession?.category === ClassSessionCategory.VIRTUAL &&
    observingVirtualClassId === currentClassSession.id,
);

export const selectParticipantBarVisibleClassUserIds = (state: RootState) => {
  return state.pubsub_virtual_class_state.participant_bar_visible_class_user_ids;
};

export const selectPinnedClassUserIds = (state: RootState) => {
  return state.pubsub_virtual_class_state.pinned_class_user_ids ?? [];
};

export const selectIsPinnedClassUserId = createAppSelector(
  [selectPinnedClassUserIds, (state, classUserId: string) => classUserId],
  (pinnedClassUserIds, classUserId) => {
    return pinnedClassUserIds.includes(classUserId);
  },
);

export const selectCurrentlySpeakingClassUserIds = createAppSelector(
  (state: RootState) => state.pubsub_virtual_class_state.users,
  (users) => {
    return Object.keys(users).filter((classUserId) => users[classUserId]?.isSpeaking);
  },
);

export const selectAvActiveClassUserIds = (state: RootState) => {
  return state.pubsub_virtual_class_state.av_active_class_user_ids ?? [];
};

export const selectStateLeaderClassUserId = (state: RootState) => {
  return state.pubsub_virtual_class_state.leader_class_user_id ?? null;
};

export const selectIsStateParticipantBarLeader = createAppSelector(
  [selectCurrentClassUserId, selectStateLeaderClassUserId],
  (currentClassUserId, stateLeaderClassUserId) =>
    !!currentClassUserId && currentClassUserId === stateLeaderClassUserId,
);
